import { DatePickerProps } from 'components/DatePicker/DatePicker';
import { DateRangePickerProps } from 'components/DateRangePicker/DateRangePicker';
import FutureDateRangePicker from 'components/DateRangePicker/FutureDateRangePicker';
import { RsDateRangePicker } from 'components/Rsuite/DateRangePicker';
import { useField, useFormikContext } from 'formik';
import { defaultTo } from 'lodash';
import React, { useCallback } from 'react';
import { FieldLayoutProps, withFieldLayout } from 'hoc/withFieldLayout';
import { FormikFieldProps } from 'hoc/withFormikField';

type FormikDayRangeExtraProps = {
  startDateFormikName: string;
  endDateFormikName: string;
  format?: string;
  startDatePickerDisabled?: boolean;
  validateOnPopOverClose?: boolean;
  validate?: (startDate: string, endDate: string) => void;
  onChange?: (startDate: string, endDate: string) => void;
  onStartDateChange?: (date: any) => void;
  onEndDateChange?: (date: any) => void;
};

export interface FormikDateRangePickerProps
  extends FormikDayRangeExtraProps,
    Omit<
      DatePickerProps,
      | 'disabled'
      | 'onChange'
      | 'onChangeStartDate'
      | 'onChangeEndDate'
      | 'date'
      | 'hasError'
      | 'onPopOverClosed'
    >,
    Omit<
      FormikFieldProps,
      'onChange' | 'onChangeStartDate' | 'onChangeEndDate' | 'validate'
    > {
  onPopOverClosed?: () => void | Promise<void>;
}
export interface FormikDateRangePickerWithFieldLayoutProps
  extends FormikDateRangePickerProps,
    Omit<FieldLayoutProps, 'name'> {}
export type FormikDateRangePickerFieldProps =
  | ({ withFieldLayout?: true } & FormikDateRangePickerWithFieldLayoutProps)
  | ({ withFieldLayout: false } & FormikDateRangePickerProps);

export const DateRangePickerField = withFieldLayout<
  DateRangePickerProps & FieldLayoutProps
>(RsDateRangePicker);

const useFormikDateRangePickerHook = (
  name: string,
  startDateFormikName: string,
  endDateFormikName: string,
  validateOnPopOverClose?: boolean,
  validate?: (startDate: string, endDate: string) => void,
  onChange?: (startDate: string, endDate: string) => void,
  onStartDateChange?: (date: any) => void,
  onEndDateChange?: (date: any) => void,
  onPopOverClosed?: () => void | Promise<void>,
) => {
  const { values, submitCount, setValues, setFieldTouched, validateForm } =
    useFormikContext<any>();
  const startDate = values[startDateFormikName];
  const endDate = values[endDateFormikName];

  const hanldeValidate = useCallback(
    (value) => {
      // value is undefined when date have not been modified
      const startDateToValidate = value ? value.startDate : startDate;
      const endDateToValidate = value ? value.endDate : endDate;
      return validate && validate(startDateToValidate, endDateToValidate);
    },
    [startDate, endDate, validate],
  );

  const [, { touched: formikTouched, error }] = useField({
    name,
    validate: validate ? hanldeValidate : undefined,
  });
  const touched = defaultTo(submitCount, 0) > 0 || formikTouched;

  const handleChange = useCallback(
    (startDate, endDate) => {
      setValues(
        (prev) => ({
          ...prev,
          [name]: {
            startDate,
            endDate,
          },
          [startDateFormikName]: startDate,
          [endDateFormikName]: endDate,
        }),
        !validateOnPopOverClose,
      );
      setFieldTouched(name, true, !validateOnPopOverClose);
      // formik issues 2266 workaround
      setTimeout(() => {
        onChange && onChange(startDate, endDate);
      });
    },
    [
      name,
      startDateFormikName,
      endDateFormikName,
      validateOnPopOverClose,
      setValues,
      setFieldTouched,
      onChange,
    ],
  );
  const handleChangeStartDate = useCallback(
    (date) => {
      const newStartDate = date;
      setValues(
        (prev) => ({
          ...prev,
          [name]: {
            startDate: newStartDate,
            endDate,
          },
          [startDateFormikName]: newStartDate,
        }),
        !validateOnPopOverClose,
      );
      setFieldTouched(name, true, !validateOnPopOverClose);
      // formik issues 2266 workaround
      setTimeout(() => {
        onStartDateChange && onStartDateChange(date);
      });
    },
    [
      name,
      endDate,
      startDateFormikName,
      validateOnPopOverClose,
      setValues,
      setFieldTouched,
      onStartDateChange,
    ],
  );
  const handleChangeEndDate = useCallback(
    (date) => {
      const newEndDate = date;
      setValues(
        (prev) => ({
          ...prev,
          [name]: {
            startDate,
            endDate: newEndDate,
          },
          [endDateFormikName]: newEndDate,
        }),
        !validateOnPopOverClose,
      );
      setFieldTouched(name, true, !validateOnPopOverClose);
      // formik issues 2266 workaround
      setTimeout(() => {
        onEndDateChange && onEndDateChange(date);
      });
    },
    [
      name,
      startDate,
      endDateFormikName,
      validateOnPopOverClose,
      setValues,
      setFieldTouched,
      onEndDateChange,
    ],
  );
  return {
    error,
    touched,
    startDate,
    endDate,
    onChange: handleChange,
    onStartDateChange: handleChangeStartDate,
    onEndDateChange: handleChangeEndDate,
    onPopOverClosed: validateOnPopOverClose
      ? async () => {
          onPopOverClosed && (await onPopOverClosed());
          validateForm();
        }
      : onPopOverClosed,
  };
};

const MemorizedDateRangePickerContent = React.memo(RsDateRangePicker);

export const FormikDateRangePickerField: React.FC<
  FormikDateRangePickerFieldProps
> = ({
  name,
  startDateFormikName,
  endDateFormikName,
  withFieldLayout,
  validateOnPopOverClose,
  onChange,
  onStartDateChange,
  onEndDateChange,
  validate,
  onPopOverClosed,
  ...props
}: FormikDateRangePickerFieldProps) => {
  const {
    error,
    touched,
    onChange: onDateRangeChange,
    onStartDateChange: handleChangeStartDate,
    onEndDateChange: handleChangeEndDate,
    ...otherProps
  } = useFormikDateRangePickerHook(
    name,
    startDateFormikName,
    endDateFormikName,
    validateOnPopOverClose,
    validate,
    onChange,
    onStartDateChange,
    onEndDateChange,
    onPopOverClosed,
  );
  const hasError = error !== undefined && touched;
  if (withFieldLayout === false) {
    return (
      <MemorizedDateRangePickerContent
        {...props}
        name={name}
        onChange={onDateRangeChange}
        onStartDateChange={handleChangeStartDate}
        onEndDateChange={handleChangeEndDate}
        showTimePicker={false}
        disabledHour={false}
        {...otherProps}
        hasError={hasError}
      />
    );
  } else {
    return (
      <DateRangePickerField
        {...props}
        onChange={onDateRangeChange}
        onStartDateChange={handleChangeStartDate}
        onEndDateChange={handleChangeEndDate}
        showTimePicker={false}
        disabledHour={false}
        {...otherProps}
        name={name}
        error={hasError ? error : undefined}
        hasError={hasError}
      />
    );
  }
};
